import { memo, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, InputLabel, Typography } from '@mui/material';
import { BaseImage } from '~/components/common';
import styles from './FileItem.module.scss';
import { DeleteIcon, FileIcon } from '~/assets/icons';
import classNames from 'classnames/bind';
import UploadFileDialog from '../../common/upload-file-dialog/UploadFileDialog';
import { OptionUploadFile } from '~/utils/types/common';
import { useAppDispatch } from '~/redux/hooks';
import { handleGetTags } from '~/thunks/tag/tagThunk';
import {
  CREATE_JOB_FILE,
  CREATE_CUSTOMER_FILE,
  FILE_ITEM_FOR_CUSTOMER,
  FILE_ITEM_FOR_ESTIMATE,
  FILE_ITEM_FOR_TASK,
  CREATE_ESTIMATE_FILE,
  FILE_ITEM_FOR_ESTIMATE_TEMPLATE,
} from '~/utils/constants/jobs';
import { IResponseEstimateFile } from '~/utils/interface/estimate';
const cx = classNames.bind(styles);

type FileItemProps = {
  jobId?: string;
  customerId?: string;
  form: any;
  editData?: any;
  typeData?: string;
};
function FileItem(props: FileItemProps) {
  const { editData, typeData } = props;
  const { t } = useTranslation('admin/job');
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState<boolean>(false);
  const [files, setFiles] = useState<Array<IResponseEstimateFile>>([]);
  const [folderType, setFolderType] = useState<Object>({}); // Allow folderType to be null initially
  useEffect(() => {
    if (props.customerId) {
      setFolderType({ type: 'customers' });
    } else if (props.jobId) {
      setFolderType({ type: 'jobs' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.customerId, props.jobId]);

  const optionUploadFile: OptionUploadFile = {
    uploadFile: true,
    selectFromProject: props.jobId || props.customerId ? true : false,
  };

  const hanldeSetOpenUploadFile = async (value: boolean) => {
    if (value) await dispatch(handleGetTags()).unwrap();
    setOpen(value);
  };

  useEffect(() => {
    if (typeData === FILE_ITEM_FOR_TASK) {
      if (editData?.jobFileIds && Array.isArray(editData.jobFileIds)) {
        setFiles([...editData.jobFileIds]);
      } else {
        setFiles([]);
      }
    } else if (typeData === FILE_ITEM_FOR_ESTIMATE || typeData === FILE_ITEM_FOR_ESTIMATE_TEMPLATE) {
      editData && setFiles([...editData?.estimateFiles]);
    } else if (typeData === FILE_ITEM_FOR_CUSTOMER) {
      editData && setFiles([...editData?.customerFileIds]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData]);

  useEffect(() => {
    if (typeData === FILE_ITEM_FOR_TASK) {
      const taskFiles = files.map((file) => file.id);
      props.form.setFieldValue('jobFileIds', taskFiles);
    } else if (typeData === FILE_ITEM_FOR_ESTIMATE || typeData === FILE_ITEM_FOR_ESTIMATE_TEMPLATE) {
      const estimateFiles = files.map((file) => {
        return { fileId: file.fileId };
      });
      props.form.setFieldValue('estimateFiles', estimateFiles);
    } else if (typeData === FILE_ITEM_FOR_CUSTOMER) {
      const customerFiles = files.map((file) => file.id);

      props.form.setFieldValue('customerFileIds', customerFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const handleDeleteImage = (index: number) => {
    const newFiles = files.filter((obj, key) => {
      return key !== index;
    });
    setFiles(newFiles);
  };

  const handleSelectedFiles = (files: any) => {
    setFiles(files);
  };
  let fileType;
  if (typeData === FILE_ITEM_FOR_CUSTOMER) {
    fileType = CREATE_CUSTOMER_FILE;
  } else if (typeData === FILE_ITEM_FOR_TASK || typeData === FILE_ITEM_FOR_ESTIMATE) {
    fileType = CREATE_JOB_FILE;
  } else if (typeData === FILE_ITEM_FOR_ESTIMATE_TEMPLATE) {
    fileType = CREATE_ESTIMATE_FILE;
  }

  return (
    <Box className={cx('file-container')}>
      {typeData === FILE_ITEM_FOR_TASK && <Box className='text-[13px] pb-[13px]'>{t('estimate.fileItem.title')}</Box>}
      {(typeData === FILE_ITEM_FOR_ESTIMATE || typeData === FILE_ITEM_FOR_ESTIMATE_TEMPLATE) && (
        <Box className='bg-[#F3F9FF] w-full my-4 p-5 font-[600] text-[14px] border-y-2 border-solid border-y-[#e5eaef]'>
          {t('estimate.fileItem.title')}
        </Box>
      )}
      <Box className={cx('box-file-container')}>
        <Box className='pl-5'>
          <Button color='primary' variant='contained' onClick={() => hanldeSetOpenUploadFile(true)}>
            {t('estimate.fileItem.uploadFile')}
          </Button>
        </Box>
        <Box className='flex flex-wrap justify-between'>
          {files?.map((file, index) => (
            <Box key={index} className='my-2 relative w-1/2 px-5'>
              <IconButton className='absolute right-[15px] top-0' onClick={() => handleDeleteImage(index)}>
                <DeleteIcon className='w-[12px] h-[12px]' />
              </IconButton>
              <Box className='flex justify-flex-start w-full border-2 border-solid'>
                <Box onClick={() => window.open(file?.url)} className='w-[100px] h-[100px]'>
                  {file?.type === 'IMAGE' ? (
                    <BaseImage src={file?.url} className='w-full h-full object-cover' />
                  ) : (
                    <FileIcon className='mx-auto my-[15px] w-[70px] h-[70px]' />
                  )}
                </Box>
                <Box className='block border-l-2 px-[10px] flex-1'>
                  <Box className='flex mt-3 w-full'>
                    <InputLabel className='w-[50px] content-center	'> {t('estimate.fileItem.name')}</InputLabel>
                    <Typography>{file.name}</Typography>
                  </Box>
                  <Box className='flex mt-3 w-full'>
                    <InputLabel className='w-[50px] content-center'> {t('estimate.fileItem.note')}</InputLabel>
                    <Typography>{file.note}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        <UploadFileDialog
          jobId={props.jobId}
          customerId={props.customerId}
          open={open}
          setOpen={hanldeSetOpenUploadFile}
          optionUploadFile={optionUploadFile}
          type={fileType}
          selectedFiles={handleSelectedFiles}
          editData={files}
          data={folderType}
        />
      </Box>
    </Box>
  );
}

export default memo(FileItem);
